import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Avatar,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Search,
  FilterList,
  Sort,
  MoreVert,
  Delete,
  PlayArrow,
  Star,
  StarBorder,
  Share,
  Edit,
  Visibility,
  VisibilityOff,
  SelectAll,
  Clear,
  Movie,
  Tv,
  Public,
  Lock,
  Add
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { watchlistItemsByWatchlistIDAndCreatedAt } from '../../graphql/queries';
import { deleteWatchlistItem, updateWatchlistItem } from '../../graphql/mutations';
import { getMovieById, getShowById } from '../../services/mongoApi';

const WatchlistViewer = ({ watchlist, onClose, onEdit, onUpdate }) => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const theme = useTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrichedItems, setEnrichedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('dateAdded');
  const [filterBy, setFilterBy] = useState('all');
  const [selectedItems, setSelectedItems] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [bulkActionAnchor, setBulkActionAnchor] = useState(null);

  useEffect(() => {
    if (watchlist) {
      loadWatchlistItems();
    }
  }, [watchlist]);

  useEffect(() => {
    filterAndSortItems();
  }, [enrichedItems, searchQuery, sortBy, filterBy]);

  const loadWatchlistItems = async () => {
    setLoading(true);
    try {
      const client = generateClient();
      const result = await client.graphql({
        query: watchlistItemsByWatchlistIDAndCreatedAt,
        variables: {
          watchlistID: watchlist.id,
          limit: 100
        }
      });
      
      const watchlistItems = result.data?.watchlistItemsByWatchlistIDAndCreatedAt?.items || [];
      setItems(watchlistItems);
      
      // Enrich items with detailed content data from mongo API
      await enrichItemsWithContentData(watchlistItems);
    } catch (error) {
      console.error('Error loading watchlist items:', error);
      addNotification({ type: 'error', message: 'Failed to load watchlist items' });
    } finally {
      setLoading(false);
    }
  };
  
  const enrichItemsWithContentData = async (watchlistItems) => {
    try {
      const enrichedData = await Promise.all(
        watchlistItems.map(async (item) => {
          if (item.contentType === 'MOVIE' && item.movieID) {
            try {
              const detailedMovie = await getMovieById(item.movieID);
              return {
                ...item,
                movie: {
                  ...item.movie,
                  ...detailedMovie,
                  // Preserve GraphQL fields if they exist
                  id: item.movieID
                }
              };
            } catch (error) {
              console.error(`Error fetching movie details for ${item.movieID}:`, error);
              return item; // Return original item if API call fails
            }
          } else if (item.contentType === 'SHOW' && item.showID) {
            try {
              const detailedShow = await getShowById(item.showID);
              return {
                ...item,
                show: {
                  ...item.show,
                  ...detailedShow,
                  // Preserve GraphQL fields if they exist
                  id: item.showID
                }
              };
            } catch (error) {
              console.error(`Error fetching show details for ${item.showID}:`, error);
              // Fallback to GraphQL data if API call fails
              return {
                ...item,
                show: {
                  ...item.show,
                  id: item.showID
                }
              };
            }
          }
          return item; // Return as-is for other content types
        })
      );
      
      setEnrichedItems(enrichedData);
    } catch (error) {
      console.error('Error enriching items with content data:', error);
      setEnrichedItems(watchlistItems); // Fallback to original items
    }
  };

  const filterAndSortItems = () => {
    let filtered = [...(enrichedItems.length > 0 ? enrichedItems : items)];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(item => {
        const content = item.movie || item.show;
        return content?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
               content?.genre?.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }

    // Apply content type filter
    if (filterBy !== 'all') {
      filtered = filtered.filter(item => item.contentType === filterBy);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'dateAdded':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'name':
          const nameA = (a.movie || a.show)?.name || '';
          const nameB = (b.movie || b.show)?.name || '';
          return nameA.localeCompare(nameB);
        case 'rating':
          const ratingA = (a.movie || a.show)?.rating || 0;
          const ratingB = (b.movie || b.show)?.rating || 0;
          return ratingB - ratingA;
        case 'hypeRating':
          const hypeA = (a.movie || a.show)?.hypeRating || 0;
          const hypeB = (b.movie || b.show)?.hypeRating || 0;
          return hypeB - hypeA;
        case 'priority':
          return (b.priority || 0) - (a.priority || 0);
        default:
          return 0;
      }
    });

    setFilteredItems(filtered);
  };

  const handleRemoveItem = async (item) => {
    try {
      const client = generateClient();
      const result = await client.graphql({
        query: `
          mutation DeleteWatchlistItem($input: DeleteWatchlistItemInput!) {
            deleteWatchlistItem(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: { id: item.id }
        }
      });
      
      setItems(prev => prev.filter(i => i.id !== item.id));
      setEnrichedItems(prev => prev.filter(i => i.id !== item.id));
      
      // Call onUpdate to refresh parent component
      if (onUpdate) {
        onUpdate();
      }
      
      addNotification({ 
        type: 'success', 
        message: `Removed from ${watchlist.name}` 
      });
    } catch (error) {
      console.error('Error removing item:', error);
      addNotification({ type: 'error', message: 'Failed to remove item' });
    }
  };

  const handleUpdatePriority = async (item, priority) => {
    try {
      const client = generateClient();
      await client.graphql({
        query: `
          mutation UpdateWatchlistItem($input: UpdateWatchlistItemInput!) {
            updateWatchlistItem(input: $input) {
              id
              priority
            }
          }
        `,
        variables: {
          input: {
            id: item.id,
            priority
          }
        }
      });
      
      setItems(prev => prev.map(i => 
        i.id === item.id ? { ...i, priority } : i
      ));
      setEnrichedItems(prev => prev.map(i => 
        i.id === item.id ? { ...i, priority } : i
      ));
      
      addNotification({ 
        type: 'success', 
        message: 'Priority updated' 
      });
    } catch (error) {
      console.error('Error updating priority:', error);
      addNotification({ type: 'error', message: 'Failed to update priority' });
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedItems.length === 0) return;

    try {
      const client = generateClient();
      
      if (action === 'remove') {
        await Promise.all(
          selectedItems.map(itemId =>
            client.graphql({
              query: `
                mutation DeleteWatchlistItem($input: DeleteWatchlistItemInput!) {
                  deleteWatchlistItem(input: $input) {
                    id
                  }
                }
              `,
              variables: { input: { id: itemId } }
            })
          )
        );
        
        setItems(prev => prev.filter(item => !selectedItems.includes(item.id)));
        setEnrichedItems(prev => prev.filter(item => !selectedItems.includes(item.id)));
        
        // Call onUpdate to refresh parent component
        if (onUpdate) {
          onUpdate();
        }
        
        addNotification({ 
          type: 'success', 
          message: `Removed ${selectedItems.length} items from ${watchlist.name}` 
        });
      }
      
      setSelectedItems([]);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      addNotification({ type: 'error', message: 'Failed to perform bulk action' });
    }
    
    setBulkActionAnchor(null);
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map(item => item.id));
    }
  };

  const getContentImage = (item) => {
    const content = item.movie || item.show;
    // Use mongo API poster path if available, fallback to GraphQL poster
    return content?.posterPath || content?.poster_path || content?.poster || '/placeholder-movie.jpg';
  };

  const getContentTitle = (item) => {
    const content = item.movie || item.show;
    return content?.name || 'Unknown Title';
  };

  const getContentDetails = (item) => {
    const content = item.movie || item.show;
    return {
      genre: content?.genre || content?.genres || 'Unknown',
      rating: content?.rating || content?.vote_average || 0,
      hypeRating: content?.hypeRating || 0,
      year: content?.release_date ? new Date(content.release_date).getFullYear() : 
            content?.releaseDate ? new Date(content.releaseDate).getFullYear() :
            content?.first_air_date ? new Date(content.first_air_date).getFullYear() : 'Unknown',
      overview: content?.overview || content?.summary || '',
      runtime: content?.runtime || content?.duration || content?.episode_run_time?.[0] || null
    };
  };

  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'error';
    if (priority >= 5) return 'warning';
    if (priority >= 3) return 'info';
    return 'default';
  };

  const getPriorityLabel = (priority) => {
    if (priority >= 8) return 'High Priority';
    if (priority >= 5) return 'Medium Priority';
    if (priority >= 3) return 'Low Priority';
    return 'No Priority';
  };

  if (!watchlist) return null;

  return (
    <Dialog 
      open={Boolean(watchlist)} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen={window.innerWidth < 900}
      PaperProps={{
        sx: {
          background: theme.palette.mode === 'dark' 
            ? 'rgba(0, 0, 0, 0.9)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 193, 7, 0.2)'
            : '1px solid rgba(25, 118, 210, 0.2)',
          borderRadius: 2,
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '90vh' }
        }
      }}
    >
      <DialogTitle sx={{
        background: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.5)' 
          : 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(10px)',
        borderBottom: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 193, 7, 0.1)'
          : '1px solid rgba(25, 118, 210, 0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h6">{watchlist.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {watchlist.isDefault && (
                  <Chip label="Default" size="small" color="primary" />
                )}
                {watchlist.isPublic ? (
                  <Chip icon={<Public />} label="Public" size="small" />
                ) : (
                  <Chip icon={<Lock />} label="Private" size="small" />
                )}
                <Typography variant="body2" color="text.secondary">
                  {filteredItems.length} of {enrichedItems.length > 0 ? enrichedItems.length : items.length} items
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onEdit && (
              <Button startIcon={<Edit />} onClick={() => onEdit(watchlist)}>
                Edit
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{
        background: 'transparent'
      }}>
        {/* Search and Filter Controls */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search movies and shows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            sx={{ 
              minWidth: 250,
              '& .MuiOutlinedInput-root': {
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(0, 0, 0, 0.5)' 
                  : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 193, 7, 0.2)'
                  : '1px solid rgba(25, 118, 210, 0.2)'
              }
            }}
          />
          
          <FormControl sx={{ 
            minWidth: 120,
            '& .MuiOutlinedInput-root': {
              background: theme.palette.mode === 'dark' 
                ? 'rgba(0, 0, 0, 0.5)' 
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 193, 7, 0.2)'
                : '1px solid rgba(25, 118, 210, 0.2)'
            },
            '& .MuiInputLabel-root': {
              color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2'
            }
          }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              label="Filter"
            >
              <MenuItem value="all">All Content</MenuItem>
              <MenuItem value="MOVIE">Movies Only</MenuItem>
              <MenuItem value="SHOW">Shows Only</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ 
            minWidth: 140,
            '& .MuiOutlinedInput-root': {
              background: theme.palette.mode === 'dark' 
                ? 'rgba(0, 0, 0, 0.5)' 
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 193, 7, 0.2)'
                : '1px solid rgba(25, 118, 210, 0.2)'
            },
            '& .MuiInputLabel-root': {
              color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2'
            }
          }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Sort By"
            >
              <MenuItem value="dateAdded">Date Added</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="rating">Rating</MenuItem>
              <MenuItem value="hypeRating">Hype Rating</MenuItem>
              <MenuItem value="priority">Priority</MenuItem>
            </Select>
          </FormControl>

          {selectedItems.length > 0 && (
            <Button
              startIcon={<Clear />}
              onClick={() => setBulkActionAnchor(document.body)}
              color="error"
            >
              {selectedItems.length} Selected
            </Button>
          )}
        </Box>

        {/* Bulk Selection Controls */}
        {filteredItems.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
              checked={selectedItems.length === filteredItems.length}
              indeterminate={selectedItems.length > 0 && selectedItems.length < filteredItems.length}
              onChange={handleSelectAll}
            />
            <Typography variant="body2">
              Select All ({filteredItems.length} items)
            </Typography>
          </Box>
        )}

        {/* Content Grid */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Loading watchlist...</Typography>
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {(enrichedItems.length > 0 ? enrichedItems.length : items.length) === 0 ? 'No items in this watchlist' : 'No items match your search'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(enrichedItems.length > 0 ? enrichedItems.length : items.length) === 0 
                ? 'Start adding movies and shows to organize your content'
                : 'Try adjusting your search or filter criteria'
              }
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredItems.map((item) => {
              const content = item.movie || item.show;
              const details = getContentDetails(item);
              const isSelected = selectedItems.includes(item.id);
              
              return (
                <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2} key={item.id}>
                  <Card 
                    sx={{ 
                      position: 'relative',
                      cursor: 'pointer',
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : (theme.palette.mode === 'dark'
                        ? 'rgba(255, 193, 7, 0.2)'
                        : 'rgba(25, 118, 210, 0.2)'),
                      background: theme.palette.mode === 'dark' 
                        ? 'rgba(0, 0, 0, 0.8)' 
                        : 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(20px)',
                      '&:hover': {
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 12px 40px rgba(255, 193, 7, 0.4)'
                          : '0 12px 40px rgba(25, 118, 210, 0.4)',
                        transform: 'translateY(-4px)',
                        border: theme.palette.mode === 'dark'
                          ? '1px solid rgba(255, 193, 7, 0.6)'
                          : '1px solid rgba(25, 118, 210, 0.6)'
                      },
                      transition: 'all 0.3s ease-in-out'
                    }}
                    onClick={() => {
                      if (item.contentType === 'MOVIE' && item.movieID) {
                        navigate(`/movie/${item.movieID}`);
                      } else if (item.contentType === 'SHOW' && item.showID) {
                        navigate(`/show/${item.showID}`);
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(prev => [...prev, item.id]);
                          } else {
                            setSelectedItems(prev => prev.filter(id => id !== item.id));
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 1,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&.Mui-checked': {
                            color: 'primary.main'
                          }
                        }}
                      />
                      
                      <CardMedia
                        component="img"
                        height="320"
                        image={getContentImage(item)}
                        alt={getContentTitle(item)}
                        sx={{ 
                          objectFit: 'cover',
                          borderRadius: '8px 8px 0 0',
                          width: '100%',
                          aspectRatio: '2/3'
                        }}
                      />
                      
                      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <Chip
                          icon={item.contentType === 'MOVIE' ? <Movie /> : <Tv />}
                          label={item.contentType === 'MOVIE' ? 'Movie' : 'Show'}
                          size="small"
                          sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: 'white' }}
                        />
                      </Box>
                      
                      {item.priority > 0 && (
                        <Box sx={{ position: 'absolute', bottom: 8, left: 8 }}>
                          <Chip
                            label={getPriorityLabel(item.priority)}
                            size="small"
                            color={getPriorityColor(item.priority)}
                          />
                        </Box>
                      )}
                    </Box>
                    
                    <CardContent sx={{ p: 2 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.2,
                          minHeight: '2.4em'
                        }}
                        title={getContentTitle(item)}
                      >
                        {getContentTitle(item)}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 1,
                          fontWeight: 500,
                          color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2'
                        }}
                      >
                        {details.genre}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star fontSize="small" sx={{ color: '#FFD700' }} />
                          <Typography variant="body2" fontWeight={600}>
                            {details.rating.toFixed(1)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {details.year}
                        </Typography>
                      </Box>
                    </CardContent>
                    
                    <CardActions sx={{ pt: 0, px: 2, pb: 2, justifyContent: 'space-between' }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.contentType === 'MOVIE' && item.movieID) {
                            navigate(`/movie/${item.movieID}`);
                          } else if (item.contentType === 'SHOW' && item.showID) {
                            navigate(`/show/${item.showID}`);
                          }
                        }}
                        sx={{
                          background: theme.palette.mode === 'dark'
                            ? 'linear-gradient(45deg, #FFD700, #FFA000)'
                            : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                          color: 'white',
                          fontWeight: 600,
                          '&:hover': {
                            background: theme.palette.mode === 'dark'
                              ? 'linear-gradient(45deg, #FFA000, #FFD700)'
                              : 'linear-gradient(45deg, #42a5f5, #1976d2)'
                          }
                        }}
                      >
                        View Details
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedItem(item);
                          setAnchorEl(e.currentTarget);
                        }}
                        sx={{
                          color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2'
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      {/* Item Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleUpdatePriority(selectedItem, 10)}>
          <ListItemIcon><Star color="error" /></ListItemIcon>
          <ListItemText>High Priority</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleUpdatePriority(selectedItem, 5)}>
          <ListItemIcon><Star color="warning" /></ListItemIcon>
          <ListItemText>Medium Priority</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleUpdatePriority(selectedItem, 2)}>
          <ListItemIcon><Star color="info" /></ListItemIcon>
          <ListItemText>Low Priority</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleUpdatePriority(selectedItem, 0)}>
          <ListItemIcon><StarBorder /></ListItemIcon>
          <ListItemText>No Priority</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleRemoveItem(selectedItem);
            setAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><Delete color="error" /></ListItemIcon>
          <ListItemText>Remove from Watchlist</ListItemText>
        </MenuItem>
      </Menu>

      {/* Bulk Actions Menu */}
      <Menu
        anchorEl={bulkActionAnchor}
        open={Boolean(bulkActionAnchor)}
        onClose={() => setBulkActionAnchor(null)}
      >
        <MenuItem onClick={() => handleBulkAction('remove')} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete color="error" /></ListItemIcon>
          <ListItemText>Remove Selected Items</ListItemText>
        </MenuItem>
      </Menu>
    </Dialog>
  );
};

export default WatchlistViewer;